import toast from 'react-hot-toast';

import { ProgressUpdate } from '../../../blocks/ProgressUpdateCard/types';
import { useCopyShadowCheckIns } from '../hooks/useCopyShadowCheckIns';
import { useDeleteCheckIn } from '../hooks/useDeleteCheckIn';
import { GetTicketCheckInsResponse } from '../types/queryTypes';
import { CheckInData, CheckInTypeEnum } from '../types/types';
import { isTicketUpdate, transformCheckIn } from '../utils';

import { CheckInSection } from './CheckInSection';

export interface CheckInOverviewProps {
  userId: string | undefined;
  shadowId: string | null | undefined;
  userCheckInId: string | null | undefined;
  selectedTab: number;
  ticketCheckIns: GetTicketCheckInsResponse[];
  shadowTicketCheckIns: GetTicketCheckInsResponse[];
  setFormState: (state: any) => void;
}

export function CheckInOverview({
  userId,
  shadowId,
  userCheckInId,
  selectedTab,
  ticketCheckIns,
  shadowTicketCheckIns,
  setFormState,
}: CheckInOverviewProps): JSX.Element {
  const deleteCheckInMutation = useDeleteCheckIn();
  const handleDelete = async (checkIn: ProgressUpdate): Promise<void> => {
    await deleteCheckInMutation.mutateAsync({
      id: checkIn.id,
      checkInId: checkIn.checkInId || '',
      isLast: ticketCheckIns.length === 1,
    });
  };
  const copyCheckInMutation = useCopyShadowCheckIns();
  const handleCopy = (shadowCheckIns: CheckInData[]): void => {
    try {
      if (!userId) {
        toast.error('User ID is required');
        return;
      }
      if (!shadowId) {
        toast.error('No shadow ID found');
        return;
      }
      shadowCheckIns.forEach((data) => {
        copyCheckInMutation.mutate(
          {
            dataArray: [data],
            userId,
            userCheckInId: userCheckInId || '',
            shadowId,
            dailySequence: selectedTab + 1,
          },
          {
            onSuccess: () => {
              toast.success('Update copied successfully');
            },
            onError: (error: Error) => {
              console.error(error);
              toast.error('Failed to copy update, please try again');
            },
          },
        );
      });
    } catch (error) {
      console.error('Unexpected error during submission:', error);
      toast.error('An unexpected error occurred');
    }
  };
  const handleEdit = (update: ProgressUpdate): void => {
    const isTicket = isTicketUpdate(update);
    setFormState({
      id: update.id,
      type: isTicket ? CheckInTypeEnum.TICKET : CheckInTypeEnum.AD_HOC,
      ticketId: update.type === CheckInTypeEnum.TICKET ? update.ticketId : '',
      title: update.title,
      description: update.description,
      hasBlockers: update.type === CheckInTypeEnum.TICKET && !!update.blockers,
      blockers: update.type === CheckInTypeEnum.TICKET ? update.blockers : '',
      wipMaterials:
        update.type === CheckInTypeEnum.TICKET ? update.wipLinks : '',
      prevProgress:
        update.type === CheckInTypeEnum.TICKET ? update.progress?.previous : 0,
      progress:
        update.type === CheckInTypeEnum.TICKET ? update.progress?.current : 0,
      isUpdate: true,
    });
  };

  // Get the appropriate title based on selectedTab
  const getTitle = (tab: number): string => {
    return tab === 0
      ? 'Mid-day Check-in Overview'
      : 'End-day Check-in Overview';
  };

  const progressUpdates: ProgressUpdate[] =
    ticketCheckIns.map(transformCheckIn);

  const shadowUpdates: ProgressUpdate[] = shadowTicketCheckIns
    .filter((checkIn) => !checkIn.is_copied)
    .map(transformCheckIn);
  return (
    <div className="flex z-0 flex-col gap-y-4 w-full max-h-[80vh] max-w-[36%] self-stretch">
      <CheckInSection
        progressUpdates={progressUpdates}
        hasShadow={!!shadowId}
        title={getTitle(selectedTab)}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
      />
      {shadowId && (
        <CheckInSection
          progressUpdates={shadowUpdates}
          hasShadow={!!shadowId}
          title="Check-In from Shadow Engineer"
          showShadowFeatures
          handleCopy={handleCopy}
        />
      )}
    </div>
  );
}
